import { useState, useMemo, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SEO from '../../components/feature/SEO';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const SERVICES_LIST = [
  'Full Rewire', 'New Installation', 'Lighting', 'Fuse Board Upgrade',
  'EV Charger', 'Fault Finding', 'Commercial', 'Consumer Unit',
  'Smart Home', 'EICR / Condition Report', 'Bathroom Electrics', 'Solar / Battery',
  'PAT Testing', 'Fire Alarm Testing', 'Emergency Lighting', 'HMO Compliance',
  'Socket & Switch', 'Outdoor Lighting', 'Security Lighting', 'Heating Controls',
];

const CONTACT_DETAILS = [
  {
    icon: 'ri-phone-fill',
    label: 'Phone',
    value: '01529 265060',
    sub: 'Mon–Fri 8am–6pm · Sat 9am–4pm',
    href: 'tel:01529265060',
    cta: 'Call Now',
  },
  {
    icon: 'ri-mail-fill',
    label: 'Email',
    value: 'info@quantum-electrical.uk',
    sub: 'We reply within 2 hours in business hours',
    href: 'mailto:info@quantum-electrical.uk',
    cta: 'Send Email',
  },
  {
    icon: 'ri-map-pin-2-fill',
    label: 'Based In',
    value: 'Sleaford, Lincolnshire',
    sub: '2-7 St Vincents, Sleaford NG34',
    href: 'https://maps.google.com/?q=Sleaford,Lincolnshire',
    cta: 'View Map',
  },
  {
    icon: 'ri-alarm-warning-fill',
    label: 'Emergency?',
    value: '24/7 Emergency Line',
    sub: 'Call 01529 265060 anytime',
    href: 'tel:01529265060',
    cta: 'Emergency Call',
  },
];

const HOURS = [
  { day: 'Monday', hours: '8:00am – 6:00pm', open: true },
  { day: 'Tuesday', hours: '8:00am – 6:00pm', open: true },
  { day: 'Wednesday', hours: '8:00am – 6:00pm', open: true },
  { day: 'Thursday', hours: '8:00am – 6:00pm', open: true },
  { day: 'Friday', hours: '8:00am – 6:00pm', open: true },
  { day: 'Saturday', hours: '9:00am – 4:00pm', open: true },
  { day: 'Sunday', hours: 'Emergency only', open: false },
];

const SERVICE_AREAS = [
  'Sleaford', 'Grantham', 'Boston', 'Spalding', 'Lincoln', 'Newark-on-Trent',
  'Stamford', 'Bourne', 'Heckington', 'Ruskington', 'Ancaster', 'Cranwell',
  'Leadenham', 'Navenby', 'Metheringham', 'Kirkby la Thorpe',
];

type FormState = 'idle' | 'sending' | 'success' | 'error';

export default function ContactPage() {
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);

  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';

  const jsonLd = useMemo(() => [
    {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Quantum Electrical Services',
      url: `${siteUrl}/contact`,
      description: 'Contact page for Quantum Electrical Services, NAPIT registered electricians in Sleaford, Lincolnshire.',
      mainEntity: {
        '@type': 'ElectricalContractor',
        name: 'Quantum Electrical Services',
        telephone: '+441529265060',
        email: 'info@quantum-electrical.uk',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2-7 St Vincents',
          addressLocality: 'Sleaford',
          addressRegion: 'Lincolnshire',
          postalCode: 'NG34',
          addressCountry: 'GB',
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '08:00',
            closes: '18:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Saturday'],
            opens: '09:00',
            closes: '16:00',
          },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Contact', item: `${siteUrl}/contact` },
      ],
    },
  ], [siteUrl]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;

    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));

    setFormState('sending');
    try {
      const res = await fetch('https://readdy.ai/api/form/d73ssgivu4krcbn3askg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormState(res.ok ? 'success' : 'error');
      if (res.ok) form.reset();
    } catch {
      setFormState('error');
    }
  };

  return (
    <>
      <SEO
        title="Contact Quantum Electrical Services Sleaford | 01529 265060"
        description="Contact Quantum Electrical Services in Sleaford, Lincolnshire. Call 01529 265060 for a free quote. NAPIT registered electricians. Email info@quantum-electrical.uk."
        keywords="contact electrician Sleaford, quote electrical Sleaford, Quantum Electrical contact, electrician Lincolnshire phone"
        canonicalPath="/contact"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #07101F 0%, #0D1E35 60%, #07101F 100%)' }}>
          <div className="absolute inset-0 opacity-20 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #C8A96A 0%, transparent 50%), radial-gradient(circle at 80% 50%, #A8894F 0%, transparent 50%)' }} />
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <span className="inline-flex items-center gap-2 bg-[#C8A96A]/10 border border-[#C8A96A]/25 text-[#C8A96A] text-xs tracking-widest uppercase px-4 py-2 rounded-full mb-6">
              <i className="ri-message-3-fill text-xs" />
              Get In Touch
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white leading-tight mb-6">
              Contact{' '}
              <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">Quantum Electrical</span>
            </h1>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8">
              Free, no-obligation quotes. NAPIT registered electricians covering Sleaford and all of Lincolnshire. Call, email or complete the form — we&apos;ll get back to you within 2 hours.
            </p>
            <nav className="flex items-center justify-center gap-2 text-xs text-white/40">
              <Link to="/" className="hover:text-[#C8A96A] transition-colors duration-200">Home</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-[#C8A96A]">Contact</span>
            </nav>
          </div>
        </section>

        {/* Quick contact cards */}
        <section className="py-14 bg-[#07101F]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CONTACT_DETAILS.map((item) => (
                <ContactCard key={item.label} item={item} />
              ))}
            </div>
          </div>
        </section>

        {/* Main content: form + info */}
        <section className="py-20 bg-[#071120]">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">

              {/* Left: info column */}
              <div className="lg:col-span-2 flex flex-col gap-10">

                {/* Business hours */}
                <HoursCard />

                {/* Service areas */}
                <ServiceAreasCard />

                {/* Certifications */}
                <CertificationsCard />
              </div>

              {/* Right: form */}
              <div className="lg:col-span-3">
                <FormSection
                  formState={formState}
                  charCount={charCount}
                  setCharCount={setCharCount}
                  handleSubmit={handleSubmit}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Map */}
        <section className="bg-[#07101F]">
          <MapSection />
        </section>
      </main>

      <Footer />
    </>
  );
}

function ContactCard({ item }: { item: typeof CONTACT_DETAILS[0] }) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <a
      ref={ref as React.RefObject<HTMLAnchorElement>}
      href={item.href}
      className={`group flex flex-col gap-4 bg-[#0D1E35]/80 border border-[#C8A96A]/12 hover:border-[#C8A96A]/40 rounded-2xl p-6 transition-all duration-500 cursor-pointer ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="w-12 h-12 flex items-center justify-center bg-[#C8A96A]/12 group-hover:bg-[#C8A96A]/22 rounded-xl transition-colors duration-300 flex-shrink-0">
        <i className={`${item.icon} text-[#C8A96A] text-xl`} />
      </div>
      <div className="flex-1">
        <p className="text-[#C8A96A] text-xs uppercase tracking-widest font-semibold mb-1">{item.label}</p>
        <p className="text-white font-semibold text-sm leading-snug">{item.value}</p>
        <p className="text-white/40 text-xs mt-1.5 leading-snug">{item.sub}</p>
      </div>
      <span className="text-[#C8A96A]/60 group-hover:text-[#C8A96A] text-xs font-semibold tracking-wide flex items-center gap-1 transition-colors duration-200">
        {item.cta}
        <i className="ri-arrow-right-line text-xs group-hover:translate-x-0.5 transition-transform duration-200" />
      </span>
    </a>
  );
}

function HoursCard() {
  const { ref, isVisible } = useScrollAnimation();
  const today = new Date().getDay();
  const dayMap: Record<number, string> = { 0: 'Sunday', 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' };
  const todayName = dayMap[today];

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`bg-[#0D1E35]/60 border border-[#C8A96A]/12 rounded-2xl p-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 flex items-center justify-center bg-[#D4AF37]/12 rounded-xl">
          <i className="ri-time-fill text-[#D4AF37] text-base" />
        </div>
        <h3 className="text-white font-bold text-sm tracking-wide uppercase">Business Hours</h3>
      </div>
      <div className="flex flex-col gap-2">
        {HOURS.map(({ day, hours, open }) => (
          <div
            key={day}
            className={`flex items-center justify-between py-2 border-b last:border-0 transition-colors ${
              day === todayName ? 'border-[#C8A96A]/20' : 'border-white/5'
            }`}
          >
            <span className={`text-sm ${day === todayName ? 'text-[#D4AF37] font-semibold' : 'text-white/60'}`}>
              {day}
              {day === todayName && (
                <span className="ml-2 text-[9px] bg-[#D4AF37]/15 border border-[#D4AF37]/30 text-[#D4AF37] px-1.5 py-0.5 rounded-full font-bold tracking-wider uppercase">Today</span>
              )}
            </span>
            <span className={`text-xs font-medium ${open ? (day === todayName ? 'text-[#D4AF37]' : 'text-white/70') : 'text-white/30 italic'}`}>
              {hours}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center gap-2 bg-green-500/8 border border-green-500/20 rounded-xl px-3 py-2.5">
        <div className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0 animate-pulse" />
        <p className="text-green-400 text-xs">Emergency line available 24/7 — <strong>01529 265060</strong></p>
      </div>
    </div>
  );
}

function ServiceAreasCard() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`bg-[#0D1E35]/60 border border-[#C8A96A]/12 rounded-2xl p-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 flex items-center justify-center bg-[#D4AF37]/12 rounded-xl">
          <i className="ri-map-pin-2-fill text-[#D4AF37] text-base" />
        </div>
        <h3 className="text-white font-bold text-sm tracking-wide uppercase">Service Areas</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {SERVICE_AREAS.map((area) => (
          <span
            key={area}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${
              area === 'Sleaford'
                ? 'border-[#C8A96A]/50 bg-[#C8A96A]/12 text-[#C8A96A] font-semibold'
                : 'border-white/10 text-white/50'
            }`}
          >
            {area}
          </span>
        ))}
      </div>
      <p className="text-white/30 text-xs mt-4">Don&apos;t see your area? Call us — we cover all of Lincolnshire.</p>
    </div>
  );
}

function CertificationsCard() {
  const { ref, isVisible } = useScrollAnimation();
  const certs = [
    { label: 'NAPIT Registered', icon: 'ri-shield-check-fill', desc: 'Approved contractor scheme member' },
    { label: 'Part P Compliant', icon: 'ri-file-certificate-fill', desc: 'Building regulations notified work' },
    { label: 'OZEV Approved', icon: 'ri-car-fill', desc: 'Office for Zero Emission Vehicles' },
    { label: '18th Edition', icon: 'ri-book-open-fill', desc: 'Latest IET wiring regulations' },
  ];
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`bg-[#0D1E35]/60 border border-[#C8A96A]/12 rounded-2xl p-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 flex items-center justify-center bg-[#D4AF37]/12 rounded-xl">
          <i className="ri-file-certificate-fill text-[#D4AF37] text-base" />
        </div>
        <h3 className="text-white font-bold text-sm tracking-wide uppercase">Certifications</h3>
      </div>
      <div className="flex flex-col gap-3">
        {certs.map(({ label, icon, desc }) => (
          <div key={label} className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#C8A96A]/10 rounded-lg flex-shrink-0">
              <i className={`${icon} text-[#C8A96A] text-sm`} />
            </div>
            <div>
              <p className="text-white/85 text-sm font-medium">{label}</p>
              <p className="text-white/35 text-xs">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FormSection({
  formState,
  charCount,
  setCharCount,
  handleSubmit,
}: {
  formState: FormState;
  charCount: number;
  setCharCount: (n: number) => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
}) {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="mb-8">
        <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">Free Quote</p>
        <h2 className="font-playfair text-4xl font-bold text-white leading-tight">
          Tell Us What You Need
        </h2>
        <p className="text-white/50 mt-3 text-sm">
          Fill in the form and we&apos;ll get back to you within 2 hours during business hours — no obligation, no pressure.
        </p>
      </div>

      <form
        id="contact-page-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="bg-[#0D1E35]/80 border border-[#C8A96A]/12 rounded-2xl p-8 flex flex-col gap-5"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Full Name *</label>
            <input
              name="name"
              type="text"
              required
              placeholder="Your name"
              className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/25"
            />
          </div>
          <div>
            <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Phone *</label>
            <input
              name="phone"
              type="tel"
              required
              placeholder="01529 xxxxxx"
              className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/25"
            />
          </div>
        </div>

        <div>
          <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Email</label>
          <input
            name="email"
            type="email"
            placeholder="your@email.com"
            className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/25"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Service Required *</label>
            <select
              name="service"
              required
              className="w-full bg-[#0D1E35] border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 cursor-pointer"
            >
              <option value="">Select a service...</option>
              {SERVICES_LIST.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Property Type</label>
            <select
              name="property_type"
              className="w-full bg-[#0D1E35] border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 cursor-pointer"
            >
              <option value="">Select type...</option>
              <option value="Domestic">Domestic</option>
              <option value="Commercial">Commercial</option>
              <option value="Rental / Landlord">Rental / Landlord</option>
              <option value="HMO">HMO</option>
              <option value="Industrial">Industrial</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">Town / Area</label>
          <input
            name="area"
            type="text"
            placeholder="e.g. Sleaford, Grantham, Boston..."
            className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/25"
          />
        </div>

        <div>
          <label className="text-white/55 text-xs uppercase tracking-wider block mb-2">
            Message ({charCount}/500)
          </label>
          <textarea
            name="message"
            rows={5}
            maxLength={500}
            onChange={(e) => setCharCount(e.target.value.length)}
            placeholder="Describe the work you need — as much detail as you can helps us give you an accurate quote..."
            className="w-full bg-white/5 border border-white/10 focus:border-[#C8A96A]/60 rounded-xl px-4 py-3 text-white text-sm outline-none transition-colors duration-300 placeholder-white/25 resize-none"
          />
          {charCount > 450 && (
            <p className="text-[#E0C78A] text-xs mt-1">{500 - charCount} characters remaining</p>
          )}
        </div>

        {formState === 'success' && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
            <i className="ri-checkbox-circle-fill text-green-400 text-xl flex-shrink-0" />
            <p className="text-green-400 text-sm">Quote request sent! We&apos;ll be in touch very soon.</p>
          </div>
        )}
        {formState === 'error' && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
            <i className="ri-error-warning-fill text-red-400 text-xl flex-shrink-0" />
            <p className="text-red-400 text-sm">Something went wrong. Please call us on 01529 265060.</p>
          </div>
        )}

        <button
          type="submit"
          disabled={formState === 'sending'}
          className="w-full bg-gradient-to-r from-[#C8A96A] to-[#A8894F] hover:from-[#E0C78A] hover:to-[#C8A96A] text-black font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer whitespace-nowrap"
        >
          {formState === 'sending' ? (
            <>
              <i className="ri-loader-4-line animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill" />
              Request Free Quote
            </>
          )}
        </button>

        <p className="text-white/25 text-xs text-center">
          No obligation · We reply within 2 hours · All work NAPIT certified
        </p>
      </form>
    </div>
  );
}

function MapSection() {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      {/* Map header */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
          <div>
            <p className="text-[#C8A96A] text-xs tracking-widest uppercase font-medium mb-3">Coverage</p>
            <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white">
              Covering Sleaford{' '}
              <span className="bg-gradient-to-r from-[#C8A96A] to-[#E0C78A] bg-clip-text text-transparent">&amp; Lincolnshire</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {['Sleaford', 'Grantham', 'Boston', 'Lincoln', 'Spalding'].map((a) => (
              <span key={a} className="text-xs border border-[#C8A96A]/25 text-[#C8A96A]/70 px-3 py-1.5 rounded-full">{a}</span>
            ))}
            <span className="text-xs border border-white/10 text-white/35 px-3 py-1.5 rounded-full">+11 more</span>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="relative h-[420px] md:h-[480px] border-t border-[#C8A96A]/10">
        <iframe
          title="Quantum Electrical Services — Sleaford, Lincolnshire service area map"
          src="https://maps.google.com/maps?q=Sleaford,Lincolnshire,UK&t=&z=11&ie=UTF8&iwloc=&output=embed"
          className="w-full h-full"
          style={{ filter: 'grayscale(30%) contrast(1.05) brightness(0.9)' }}
          loading="lazy"
        />
        {/* Overlay card */}
        <div
          className="absolute bottom-6 left-6 rounded-2xl border border-[#C8A96A]/20 px-6 py-4 flex items-center gap-4"
          style={{ background: 'rgba(7,16,31,0.92)', backdropFilter: 'blur(12px)' }}
        >
          <div className="w-10 h-10 flex items-center justify-center bg-[#C8A96A]/15 rounded-xl flex-shrink-0">
            <i className="ri-map-pin-2-fill text-[#C8A96A] text-lg" />
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Quantum Electrical Services</p>
            <p className="text-white/45 text-xs mt-0.5">2-7 St Vincents, Sleaford, NG34 · Lincolnshire</p>
          </div>
          <a
            href="tel:01529265060"
            className="ml-4 flex items-center gap-1.5 bg-gradient-to-r from-[#C8A96A] to-[#A8894F] text-[#07101F] font-bold text-xs px-4 py-2 rounded-full whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-fill text-xs" />
            Call Now
          </a>
        </div>
      </div>
    </div>
  );
}
